// Page-context snapshot — what page the user was on, who they are, what
// browser. Compiled into the AppIssue.context field on submission.

export interface IssueContext {
  route: string;
  // Which app/platform the report originated from. Lets the admin queue show a
  // Source badge without guessing from the route. The super-admin-ui always
  // reports from the admin panel.
  source?: string;
  pageTitle?: string;
  userId: string;
  userEmail: string;
  userRole: string;
  tenantId: string;
  tenantName?: string;
  productId: string;
  browser?: string;
  os?: string;
  userAgent?: string;
  viewport?: { width: number; height: number };
  appVersion?: string;
  bundleVersion?: string;
}

function detectBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return `Edge ${ua.match(/Edg\/([\d.]+)/)?.[1] || ''}`.trim();
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return `Chrome ${ua.match(/Chrome\/([\d.]+)/)?.[1] || ''}`.trim();
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return `Safari ${ua.match(/Version\/([\d.]+)/)?.[1] || ''}`.trim();
  if (/Firefox\//.test(ua)) return `Firefox ${ua.match(/Firefox\/([\d.]+)/)?.[1] || ''}`.trim();
  return 'Unknown browser';
}

function detectOS(ua: string): string {
  if (/iPhone|iPad/.test(ua)) return `iOS ${ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || ''}`.trim();
  if (/Mac OS X/.test(ua)) return `macOS ${ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') || ''}`.trim();
  if (/Android/.test(ua)) return `Android ${ua.match(/Android ([\d.]+)/)?.[1] || ''}`.trim();
  if (/Windows NT/.test(ua)) return `Windows ${ua.match(/Windows NT ([\d.]+)/)?.[1] || ''}`.trim();
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown OS';
}

function readRoute(): string {
  const hash = window.location.hash || '';
  if (hash.startsWith('#')) return hash.slice(1).split('?')[0] || '/';
  return window.location.pathname || '/';
}

function readPageTitle(): string {
  // Prefer the page header h1 if present; fall back to document.title.
  const heading = document.querySelector('main h1, header h1') as HTMLElement | null;
  if (heading?.innerText) return heading.innerText.trim().slice(0, 120);
  return (document.title || '').slice(0, 120);
}

export function captureIssueContext(args: {
  userId: string;
  userEmail: string;
  userRole: string;
  tenantId: string;
  tenantName?: string;
  productId: string;
}): IssueContext {
  const ua = navigator.userAgent || '';
  const appVersion = (window as any).__APP_VERSION__ || undefined;
  const bundleVersion = (window as any).__BUNDLE_VERSION__ || undefined;
  return {
    route: readRoute(),
    source: 'admin-panel',
    pageTitle: readPageTitle(),
    userId: args.userId,
    userEmail: args.userEmail,
    userRole: args.userRole,
    tenantId: args.tenantId,
    tenantName: args.tenantName,
    productId: args.productId,
    browser: detectBrowser(ua),
    os: detectOS(ua),
    userAgent: ua,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    appVersion,
    bundleVersion,
  };
}

// Best-effort page state snapshot — read from window.__APP_STATE__ if a host
// page populates it. Apps that use Redux can set
// `window.__APP_STATE__ = () => store.getState()` (or similar) to enable.
export function capturePageState(): string | undefined {
  try {
    const getter = (window as any).__APP_STATE__;
    const raw = typeof getter === 'function' ? getter() : getter;
    if (!raw) return undefined;
    const json = JSON.stringify(raw, (_k, v) => {
      if (typeof v === 'string' && v.length > 500) return v.slice(0, 500) + '…';
      return v;
    });
    return json.slice(0, 10_000);
  } catch {
    return undefined;
  }
}
